import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Home, Users, Receipt, Menu, BedDouble,
  CreditCard, Zap, FileText, Cpu, Wallet, Activity, Smartphone,
  ClipboardList, FileSignature, ChevronDown, PanelLeftClose, PanelLeftOpen, Wrench, Megaphone, KeyRound,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import BellDropdown from "../components/BellDropdown";
import { getAppName } from "../components/AppConfigBoot";
import SidebarUserCard from "../components/nav/SidebarUserCard";
import { useNavBadges } from "../components/nav/useNavBadges";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";

// --- Móvil (barra inferior) ---
// Móvil propietario: lo que pide acción a diario. Inicio se abre desde la casa de
// la cabecera; Casas, Suministros y el resto viven en "Más" (/owner/more).
const OWNER_TABS = [
  { key: "payments", labelKey: "nav_owner.payments", icon: Receipt, path: "/owner/payments", badge: "payments" },
  { key: "incidents", labelKey: "nav_owner.incidents_short", icon: Wrench, path: "/owner/incidents", badge: "incidents" },
  { key: "applications", labelKey: "applications.title", icon: ClipboardList, path: "/owner/applications", badge: "applications" },
  { key: "smart", labelKey: "nav_owner.smart_short", icon: Cpu, path: "/owner/smart" },
  { key: "more", labelKey: "nav_owner.more", icon: Menu, path: "/owner/more" },
];

// Destinos del inquilino (cada uno definido una sola vez).
const T = {
  home: { key: "home", labelKey: "nav_tenant.home", icon: LayoutDashboard, path: "/tenant/home" },
  my_room: { key: "my_room", labelKey: "nav_tenant.my_room", icon: BedDouble, path: "/tenant/room" },
  payments: { key: "payments", labelKey: "nav_tenant.payments", icon: CreditCard, path: "/tenant/payments", badge: "payments" },
  documents: { key: "documents", labelKey: "nav_tenant.documents", icon: FileText, path: "/tenant/documents" },
  incidents: { key: "incidents", labelKey: "nav_owner.incidents_short", icon: Wrench, path: "/tenant/incidents", badge: "incidents" },
  application: { key: "application", labelKey: "tenant_app.title", icon: ClipboardList, path: "/tenant/application" },
  contract: { key: "contract", labelKey: "contracts.tenant_title", icon: FileSignature, path: "/tenant/contract" },
  checkin: { key: "checkin", labelKey: "nav_tenant.checkin", icon: KeyRound, path: "/tenant/checkin" },
  more: { key: "more", labelKey: "nav_tenant.more", icon: Menu, path: "/tenant/more" },
};

// Móvil inquilino: Inicio se abre desde la casa de la cabecera; Documentos,
// Perfil y el resto viven en "Más" (/tenant/more).
const TENANT_TABS = [T.my_room, T.payments, T.incidents, T.more];

// Pantallas que se abren desde "Más" (marcan esa pestaña en el móvil).
const TENANT_MORE_ROUTES = ["/tenant/more", "/tenant/documents", "/tenant/application", "/tenant/contract", "/tenant/checkin", "/notifications", "/profile"];

// Escritorio inquilino: sin "Perfil" (tarjeta de usuario) ni "Notificaciones" (campana)
const TENANT_DESKTOP = [T.home, T.my_room, T.payments, T.documents, T.application, T.contract, T.checkin, T.incidents];

// --- Escritorio propietario: Inicio + grupos acordeón ---
const OWNER_HOME = { key: "dashboard", labelKey: "nav_owner.dashboard", icon: LayoutDashboard, path: "/owner/dashboard" };

const OWNER_GROUPS = [
  {
    key: "alquileres",
    labelKey: "nav_groups.alquileres",
    items: [
      { key: "applications", labelKey: "applications.title", icon: ClipboardList, path: "/owner/applications", badge: "applications" },
      { key: "contracts", labelKey: "contracts.title", icon: FileSignature, path: "/owner/contracts" },
      { key: "tenants", labelKey: "nav_owner.tenants", icon: Users, path: "/owner/tenants" },
      { key: "announcements", labelKey: "announcements.title", icon: Megaphone, path: "/owner/announcements" },
    ],
  },
  {
    key: "finanzas",
    labelKey: "nav_groups.finanzas",
    items: [
      { key: "payments", labelKey: "nav_owner.payments", icon: Receipt, path: "/owner/payments", badge: "payments" },
      { key: "utilities", labelKey: "nav_owner.utilities", icon: Zap, path: "/owner/utilities" },
      { key: "finance", labelKey: "finance.title", icon: Wallet, path: "/owner/finance" },
    ],
  },
  {
    key: "vivienda",
    labelKey: "nav_groups.vivienda",
    items: [
      { key: "houses", labelKey: "nav_owner.houses", icon: Home, path: "/owner/houses" },
      { key: "smart", labelKey: "smart.title", icon: Cpu, path: "/owner/smart" },
      { key: "documents", labelKey: "documents.title", icon: FileText, path: "/owner/documents" },
      { key: "incidents", labelKey: "incidents.title", icon: Wrench, path: "/owner/incidents", badge: "incidents" },
    ],
  },
  {
    key: "sistema",
    labelKey: "nav_groups.sistema",
    items: [
      { key: "activity", labelKey: "more.activity", icon: Activity, path: "/owner/activity" },
      { key: "settings", labelKey: "owner_settings.title", icon: Smartphone, path: "/owner/settings" },
    ],
  },
];

// Pantallas que se abren desde "Más" del propietario: las de los grupos que no
// son pestaña en el móvil, más Perfil y Notificaciones.
const OWNER_MORE_ROUTES = [
  "/owner/more", "/profile", "/notifications",
  ...OWNER_GROUPS.flatMap((g) => g.items.map((it) => it.path))
    .filter((p) => !OWNER_TABS.some((tab) => tab.path === p)),
];

const GROUPS_LS_KEY = "casarent_nav_groups_open";
const COLLAPSED_LS_KEY = "casarent_sidebar_collapsed";
const DEFAULT_OPEN = { alquileres: true, finanzas: true, vivienda: true, sistema: false };

function CountPill({ count, badgeKey }) {
  if (!count) return null;
  return (
    <span
      data-testid={`nav-badge-${badgeKey}`}
      className="min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center bg-[#b35c00] text-white"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function SideItem({ item, count, t }) {
  return (
    <NavLink to={item.path} className="side-link" data-testid={`desktop-sidebar-nav-${item.key}`}>
      {({ isActive }) => (
        <>
          <item.icon className="w-5 h-5 shrink-0" />
          <span className="flex-1">{t(item.labelKey)}</span>
          <CountPill count={count} badgeKey={item.badge} />
        </>
      )}
    </NavLink>
  );
}

function RailLink({ item, count, label }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink
          to={item.path}
          className="side-link !justify-center !px-0 relative"
          data-testid={`desktop-sidebar-nav-${item.key}`}
        >
          <item.icon className="w-5 h-5" />
          {count > 0 && (
            <span
              data-testid={`nav-dot-${item.badge}`}
              className="absolute top-1.5 right-2 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white"
            />
          )}
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const badges = useNavBadges();

  // Refleja el total de badges (solicitudes + pagos pendientes) en el icono
  // de la app cuando está instalada como PWA (launcher/dock/taskbar) -- así
  // se ve de un vistazo si hay algo pendiente sin tener que abrir la app.
  const badgeTotal = (badges.applications || 0) + (badges.payments || 0);
  useEffect(() => {
    if (typeof navigator === "undefined" || !("setAppBadge" in navigator)) return;
    if (badgeTotal > 0) {
      navigator.setAppBadge(badgeTotal).catch(() => {});
    } else {
      navigator.clearAppBadge?.().catch(() => {});
    }
  }, [badgeTotal]);

  const isOwner = user?.role === "owner";
  const mobileTabs = isOwner ? OWNER_TABS : TENANT_TABS;

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_LS_KEY) === "1"
  );
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      return { ...DEFAULT_OPEN, ...JSON.parse(localStorage.getItem(GROUPS_LS_KEY) || "{}") };
    } catch {
      return { ...DEFAULT_OPEN };
    }
  });

  // Auto-expansión del grupo que contiene la ruta activa
  useEffect(() => {
    if (!isOwner) return;
    const active = OWNER_GROUPS.find((g) =>
      g.items.some((it) => location.pathname.startsWith(it.path))
    );
    if (active) {
      setOpenGroups((prev) => (prev[active.key] ? prev : { ...prev, [active.key]: true }));
    }
  }, [location.pathname, isOwner]);

  const toggleGroup = (key) =>
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(GROUPS_LS_KEY, JSON.stringify(next));
      return next;
    });

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      localStorage.setItem(COLLAPSED_LS_KEY, c ? "0" : "1");
      return !c;
    });

  const countFor = (item) => (item.badge ? badges[item.badge] || 0 : 0);
  const groupCount = (g) => g.items.reduce((acc, it) => acc + countFor(it), 0);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    // overflow-x-clip (no "hidden"): "hidden" en cualquiera de los dos ejes
    // fuerza el otro eje a computar como "auto" (lo pide la spec de CSS
    // Overflow), así que este div de más arriba en el árbol se convierte en
    // su propio contenedor de scroll aunque nunca llegue a desbordar — y eso
    // es justo lo que rompe el <header sticky top-0> de abajo: el navegador
    // ancla el "sticky" a ESTE div (que no hace scroll de verdad) en vez de
    // al documento, así que la barra se desplaza con el resto en vez de
    // quedarse fija. "clip" recorta igual el overflow horizontal sin crear
    // ese contenedor de scroll implícito.
    <div className="min-h-screen bg-[color:var(--page-bg)] overflow-x-clip">
      {/* Sidebar de escritorio */}
      <TooltipProvider delayDuration={150}>
        <aside
          className={`hidden md:flex flex-col fixed left-0 top-0 bottom-0 bg-white text-stone-900 z-40 border-r border-stone-200 transition-[width] duration-200 ${
            collapsed ? "w-[76px] p-3" : "w-64 p-4"
          }`}
          data-testid="desktop-sidebar"
          data-collapsed={collapsed ? "true" : "false"}
        >
          <div
            className={`flex mb-4 ${collapsed ? "flex-col items-center gap-3" : "items-center gap-3"}`}
            data-testid="sidebar-header"
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold shrink-0"
              style={{ background: "var(--brand-fill)" }}
            >
              <Home className="w-5 h-5" />
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-base leading-tight">{getAppName()}</div>
                <div className="text-xs text-stone-400 capitalize truncate">
                  {isOwner ? t("common.role_owner") : t("common.role_tenant")}
                </div>
              </div>
            )}
            <div className="shrink-0" data-testid="sidebar-bell-slot">
              <BellDropdown variant="sidebar" />
            </div>
          </div>

          <nav className="sidebar-scroll flex-1 space-y-1 overflow-y-auto overflow-x-hidden">
            {/* Inicio (siempre visible, sin grupo) */}
            {isOwner ? (
              collapsed ? (
                <RailLink item={OWNER_HOME} count={0} label={t(OWNER_HOME.labelKey)} />
              ) : (
                <SideItem item={OWNER_HOME} count={0} t={t} />
              )
            ) : (
              TENANT_DESKTOP.map((item) =>
                collapsed ? (
                  <RailLink key={item.key} item={item} count={countFor(item)} label={t(item.labelKey)} />
                ) : (
                  <SideItem key={item.key} item={item} count={countFor(item)} t={t} />
                )
              )
            )}

            {/* Grupos acordeón del propietario */}
            {isOwner &&
              OWNER_GROUPS.map((g) =>
                collapsed ? (
                  <div
                    key={g.key}
                    className="pt-2 mt-2 border-t border-stone-200 space-y-1"
                    data-testid={`sidebar-rail-group-${g.key}`}
                  >
                    {g.items.map((item) => (
                      <RailLink key={item.key} item={item} count={countFor(item)} label={t(item.labelKey)} />
                    ))}
                  </div>
                ) : (
                  <div key={g.key} data-testid={`sidebar-group-${g.key}`}>
                    <button
                      onClick={() => toggleGroup(g.key)}
                      aria-expanded={!!openGroups[g.key]}
                      className="w-full flex items-center gap-2 px-3.5 py-1.5 mt-2 text-xs text-stone-500 font-medium hover:text-stone-700 transition-colors"
                      data-testid={`sidebar-group-toggle-${g.key}`}
                    >
                      <span className="flex-1 text-left">{t(g.labelKey)}</span>
                      {!openGroups[g.key] && groupCount(g) > 0 && (
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-amber-500"
                          data-testid={`sidebar-group-dot-${g.key}`}
                        />
                      )}
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform ${openGroups[g.key] ? "" : "-rotate-90"}`}
                      />
                    </button>
                    {openGroups[g.key] && (
                      <div className="space-y-1">
                        {g.items.map((item) => (
                          <SideItem key={item.key} item={item} count={countFor(item)} t={t} />
                        ))}
                      </div>
                    )}
                  </div>
                )
              )}
          </nav>

          {/* Contraer / expandir (modo riel) */}
          <button
            onClick={toggleCollapsed}
            className={`mt-2 mb-1.5 flex items-center gap-3 py-2 rounded-lg text-stone-500 hover:bg-stone-100 hover:text-stone-900 transition-colors text-xs font-medium ${
              collapsed ? "justify-center px-0" : "px-3.5"
            }`}
            data-testid="sidebar-collapse-toggle"
            title={collapsed ? t("sidebar.expand") : t("sidebar.collapse")}
          >
            {collapsed ? (
              <PanelLeftOpen className="w-5 h-5" />
            ) : (
              <>
                <PanelLeftClose className="w-5 h-5" />
                <span>{t("sidebar.collapse")}</span>
              </>
            )}
          </button>

          {/* Tarjeta de usuario */}
          <SidebarUserCard user={user} collapsed={collapsed} onLogout={doLogout} />
        </aside>
      </TooltipProvider>

      {/* Barra superior móvil (full-width, no absolute → nunca genera scroll horizontal) */}
      <header
        className="md:hidden sticky top-0 z-30 w-full bg-white text-stone-900 border-b border-stone-200 flex items-center justify-between gap-3 px-4 h-14"
        data-testid="mobile-topbar"
      >
        <NavLink
          to={isOwner ? "/owner/dashboard" : "/tenant/home"}
          className="flex items-center gap-2 min-w-0 min-h-11 -mx-1 px-1 rounded-lg transition-transform active:scale-[0.97]"
          aria-label={`${getAppName()}, ${t(isOwner ? "nav_owner.dashboard" : "nav_tenant.home")}`}
          data-testid="mobile-topbar-home"
        >
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white shrink-0"
            style={{ background: "var(--brand-fill)" }}
          >
            <Home className="w-4 h-4" />
          </div>
          <div className="font-display font-bold text-base truncate">{getAppName()}</div>
        </NavLink>
        <div className="shrink-0" data-testid="mobile-topbar-bell-slot">
          <BellDropdown variant="mobile" />
        </div>
      </header>

      <main className={`pb-24 md:pb-8 min-h-screen ${collapsed ? "md:ml-[76px]" : "md:ml-64"}`}>
        <Outlet />
      </main>

      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-stone-200 px-0 pb-safe pt-1"
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-stretch justify-around">
          {mobileTabs.map((tab) => {
            // "Más" queda marcado (y anunciado como página actual) en las
            // pantallas que se abren desde Más.
            const moreActive = tab.key === "more" && !mobileTabs.some((x) => x.key !== "more" && location.pathname.startsWith(x.path))
              && (isOwner ? OWNER_MORE_ROUTES : TENANT_MORE_ROUTES).some((r) => location.pathname === r || location.pathname.startsWith(`${r}/`));
            const count = tab.badge ? badges[tab.badge] || 0 : 0;
            // NavLink solo pone aria-current en su propia ruta: "Más" usa Link
            // para poder marcarse también en sus subpantallas.
            const TabLink = tab.key === "more" ? Link : NavLink;
            return (
              <TabLink
                key={tab.key}
                to={tab.path}
                className={`tab-item flex-1 min-w-0 relative !px-0 !min-w-0${moreActive ? " is-active" : ""}`}
                aria-current={moreActive ? "page" : undefined}
                data-testid={`mobile-bottom-nav-${tab.key}`}
              >
                <span className="relative">
                  <tab.icon className="w-5 h-5" />
                  {count > 0 && (
                    <span
                      data-testid={`mobile-nav-badge-${tab.badge}`}
                      className="absolute -top-1.5 -right-3 min-w-[16px] h-4 px-1 rounded-full bg-[#b35c00] text-white text-[10px] font-bold flex items-center justify-center"
                    >
                      {count > 99 ? "99+" : count}
                    </span>
                  )}
                </span>
                <span className={`text-xs font-semibold leading-tight tracking-[-0.02em] max-[400px]:tracking-[-0.04em] text-center max-w-full ${mobileTabs.length > 4 ? "max-[359px]:tracking-[-0.085em]" : ""}`}>{t(tab.labelKey)}</span>
              </TabLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
